// Public keys as multikeys (did_toolkit.keys): base58btc of a multicodec prefix
// and the raw key. Ed25519 0xed01 + 32 bytes; P-256 0x8024 + 33-byte compressed point.
import { p256 } from "@noble/curves/p256";
import { b58, b58Decode, concat } from "./encoding";

export type KeyKind = "Ed25519" | "P-256";
export interface PublicKey { kind: KeyKind; raw: Uint8Array }

const PREFIX: Record<KeyKind, Uint8Array> = {
  "Ed25519": new Uint8Array([0xed, 0x01]),
  "P-256": new Uint8Array([0x80, 0x24]),
};

export function toMultikey(pk: PublicKey): string {
  return "z" + b58(concat(PREFIX[pk.kind], pk.raw));
}

export function fromMultikey(mk: string): PublicKey {
  if (!mk.startsWith("z")) throw new Error("expected base58btc 'z' prefix");
  const data = b58Decode(mk.slice(1));
  for (const kind of Object.keys(PREFIX) as KeyKind[]) {
    const p = PREFIX[kind];
    if (data[0] === p[0] && data[1] === p[1]) {
      const raw = data.slice(2);
      if (kind === "Ed25519" && raw.length !== 32) throw new Error("Ed25519 multikey must hold 32 bytes");
      if (kind === "P-256") p256.ProjectivePoint.fromHex(raw); // validates the point
      return { kind, raw };
    }
  }
  throw new Error("unknown multicodec prefix");
}

/** A JWK from a DID document (OKP/Ed25519 or EC/P-256). */
export function fromJwk(jwk: Record<string, string>): PublicKey {
  const dec = (s: string) => {
    const t = s.replace(/-/g, "+").replace(/_/g, "/");
    return Uint8Array.from(atob(t + "=".repeat((4 - (t.length % 4)) % 4)), (c) => c.charCodeAt(0));
  };
  if (jwk.kty === "OKP" && jwk.crv === "Ed25519") return { kind: "Ed25519", raw: dec(jwk.x) };
  if (jwk.kty === "EC" && jwk.crv === "P-256") {
    const point = p256.ProjectivePoint.fromAffine({ x: bytesToBig(dec(jwk.x)), y: bytesToBig(dec(jwk.y)) });
    return { kind: "P-256", raw: point.toRawBytes(true) };
  }
  throw new Error(`unsupported JWK ${jwk.kty}/${jwk.crv}`);
}

const bytesToBig = (b: Uint8Array) => BigInt("0x" + Array.from(b, (x) => x.toString(16).padStart(2, "0")).join(""));

/** did:key:<multikey> -> its key. */
export function didKeyPublicKey(did: string): PublicKey {
  if (!did.startsWith("did:key:")) throw new Error(`not a did:key: ${did}`);
  return fromMultikey(did.slice("did:key:".length).split("#")[0]);
}
